import { useEffect, useState } from 'react';
import {
  Box,
  Container,
  Typography,
  Paper,
  Stack,
} from '@mui/material';
import EmailIcon from '@mui/icons-material/Email';
import TrendingUpIcon from '@mui/icons-material/TrendingUp';
import GroupIcon from '@mui/icons-material/Group';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import ShoppingCartIcon from '@mui/icons-material/ShoppingCart';
import SecurityIcon from '@mui/icons-material/Security';
import SettingsIcon from '@mui/icons-material/Settings';
import IntegrationInstructionsIcon from '@mui/icons-material/IntegrationInstructions';
import AutoGraphIcon from '@mui/icons-material/AutoGraph';
import AnalyticsIcon from '@mui/icons-material/Analytics';
import SchoolIcon from '@mui/icons-material/School';
import MarkEmailReadIcon from '@mui/icons-material/MarkEmailRead';
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer } from 'recharts';
import CountUp from 'react-countup';
import { useSpring, animated } from 'react-spring';
import ConsultationForm from './ConsultationForm';

const features = [
  {
    icon: <EmailIcon sx={{ fontSize: 40, color: 'primary.main' }} />,
    title: 'Email Campaign Management',
    description: 'Powerful email campaign management with advanced scheduling, segmentation, and delivery tracking.'
  },
  {
    icon: <TrendingUpIcon sx={{ fontSize: 40, color: 'primary.main' }} />,
    title: 'List Management',
    description: 'Comprehensive list management tools for handling subscribers, segments, and mailing lists.'
  },
  {
    icon: <SecurityIcon sx={{ fontSize: 40, color: 'primary.main' }} />,
    title: 'Security & Compliance',
    description: 'Enterprise-grade security features and compliance with GDPR, CAN-SPAM, and other regulations.'
  },
  {
    icon: <GroupIcon sx={{ fontSize: 40, color: 'primary.main' }} />,
    title: 'Subscriber Analytics',
    description: 'Detailed analytics and reporting tools for tracking subscriber engagement and campaign performance.'
  },
  {
    icon: <ShoppingCartIcon sx={{ fontSize: 40, color: 'primary.main' }} />,
    title: 'Platform Integration',
    description: 'Seamless integration with popular email platforms, CRMs, and marketing tools.'
  },
  {
    icon: <AnalyticsIcon sx={{ fontSize: 40, color: 'primary.main' }} />,
    title: 'Custom Solutions',
    description: 'Tailored solutions to meet your specific email marketing and list management needs.'
  }
];

const serviceCategories = [
  {
    title: 'PhpList Setup & Configuration',
    icon: <SettingsIcon sx={{ fontSize: 32, color: 'primary.main' }} />,
    services: [
      'Platform Installation & Setup',
      'Server Configuration',
      'Email Integration',
      'Domain Configuration'
    ]
  },
  {
    title: 'Campaign Management',
    icon: <IntegrationInstructionsIcon sx={{ fontSize: 32, color: 'primary.main' }} />,
    services: [
      'Template Development',
      'Campaign Scheduling',
      'A/B Testing Setup',
      'Content Management'
    ]
  },
  {
    title: 'Performance & Security',
    icon: <AutoGraphIcon sx={{ fontSize: 32, color: 'primary.main' }} />,
    services: [
      'Performance Optimization',
      'Security Hardening',
      'SSL Configuration',
      'GDPR Compliance'
    ]
  },
  {
    title: 'Integration Services',
    icon: <AnalyticsIcon sx={{ fontSize: 32, color: 'primary.main' }} />,
    services: [
      'Email Platform Integration',
      'Analytics Integration',
      'API Development',
      'Custom Integration'
    ]
  },
  {
    title: 'Analytics & Reporting',
    icon: <MarkEmailReadIcon sx={{ fontSize: 32, color: 'primary.main' }} />,
    services: [
      'Analytics Dashboard Setup',
      'Custom Report Creation',
      'ROI Tracking',
      'Performance Monitoring'
    ]
  },
  {
    title: 'Training & Support',
    icon: <SchoolIcon sx={{ fontSize: 32, color: 'primary.main' }} />,
    services: [
      'Team Training',
      'Documentation',
      '24/7 Support',
      'Regular Maintenance'
    ]
  }
];

const heroMetrics = [
  { label: 'Active Subscribers', value: 15000, suffix: '+' },
  { label: 'Campaigns Sent', value: 750000, suffix: '+' },
  { label: 'Success Rate', value: 99, suffix: '%' }
];

const animatedData = [
  { month: 'Jan', subscribers: 1500, opens: 1000, clicks: 500 },
  { month: 'Feb', subscribers: 1800, opens: 1200, clicks: 600 },
  { month: 'Mar', subscribers: 2100, opens: 1400, clicks: 700 },
  { month: 'Apr', subscribers: 2400, opens: 1600, clicks: 800 },
  { month: 'May', subscribers: 2700, opens: 1800, clicks: 900 },
  { month: 'Jun', subscribers: 3000, opens: 2000, clicks: 1000 }
];

export default function PhpListPage() {
  const [isVisible, setIsVisible] = useState(false);

  useEffect(() => {
    setIsVisible(true);
  }, []);

  const fadeIn = useSpring({
    opacity: isVisible ? 1 : 0,
    transform: isVisible ? 'translateY(0)' : 'translateY(20px)',
    config: { duration: 1000 }
  });

  return (
    <Box sx={{ bgcolor: '#f7fafd', minHeight: '100vh' }}>
      {/* Hero Section */}
      <Box sx={{ bgcolor: 'primary.main', color: '#fff', py: { xs: 6, md: 10 }, position: 'relative' }}>
        <Container maxWidth="lg">
          <animated.div style={fadeIn}>
            <Box sx={{ textAlign: 'center', mb: 6 }}>
              <Typography variant="h3" fontWeight={700} gutterBottom>
                PhpList Consulting
              </Typography>
              <Typography variant="h5" sx={{ mb: 4, fontWeight: 400 }}>
                Expert deployment and optimization of your PhpList email marketing platform
              </Typography>
            </Box>

            {/* Animated Metrics */}
            <Box sx={{ display: 'flex', justifyContent: 'center', gap: 4, mb: 6 }}>
              {heroMetrics.map((metric, index) => (
                <Paper
                  key={index}
                  elevation={0}
                  sx={{
                    p: 3,
                    bgcolor: 'rgba(255, 255, 255, 0.1)',
                    borderRadius: 2,
                    minWidth: 200,
                    textAlign: 'center'
                  }}
                >
                  <Typography variant="h4" fontWeight={700} sx={{ color: 'white' }}>
                    <CountUp end={metric.value} suffix={metric.suffix} duration={2.5} />
                  </Typography>
                  <Typography variant="subtitle1" sx={{ color: 'white' }}>
                    {metric.label}
                  </Typography>
                </Paper>
              ))}
            </Box>

            {/* Animated Chart */}
            <Box sx={{ height: 300, bgcolor: 'rgba(255, 255, 255, 0.1)', borderRadius: 2, p: 3 }}>
              <ResponsiveContainer>
                <LineChart data={animatedData}>
                  <CartesianGrid strokeDasharray="3 3" stroke="rgba(255, 255, 255, 0.1)" />
                  <XAxis dataKey="month" stroke="#fff" />
                  <YAxis yAxisId="left" stroke="#fff" />
                  <YAxis yAxisId="right" orientation="right" stroke="#fff" />
                  <Tooltip
                    contentStyle={{
                      backgroundColor: 'rgba(0, 0, 0, 0.8)',
                      border: 'none',
                      borderRadius: 4
                    }}
                  />
                  <Line
                    yAxisId="left"
                    type="monotone"
                    dataKey="subscribers"
                    stroke="#0B60B1"
                    strokeWidth={2}
                    dot={false}
                  />
                  <Line
                    yAxisId="left"
                    type="monotone"
                    dataKey="opens"
                    stroke="#4CAF50"
                    strokeWidth={2}
                    dot={false}
                  />
                  <Line
                    yAxisId="right"
                    type="monotone"
                    dataKey="clicks"
                    stroke="#FF9800"
                    strokeWidth={2}
                    dot={false}
                  />
                </LineChart>
              </ResponsiveContainer>
            </Box>
          </animated.div>
        </Container>
      </Box>

      {/* Features with Image Section */}
      <Box sx={{ py: 12, bgcolor: '#fff', position: 'relative', overflow: 'hidden' }}>
        {/* Background decorative elements */}
        <Box sx={{
          position: 'absolute',
          top: 0,
          right: 0,
          width: '40%',
          height: '100%',
          background: 'linear-gradient(135deg, rgba(11,96,177,0.05) 0%, rgba(11,96,177,0) 100%)',
          clipPath: 'polygon(100% 0, 100% 100%, 0 100%, 100% 0)',
          zIndex: 0
        }} />
        <Box sx={{
          position: 'absolute',
          bottom: 0,
          left: 0,
          width: '30%',
          height: '60%',
          background: 'linear-gradient(45deg, rgba(11,96,177,0.03) 0%, rgba(11,96,177,0) 100%)',
          clipPath: 'polygon(0 0, 100% 0, 0 100%, 0 0)',
          zIndex: 0
        }} />

        <Container maxWidth="lg" sx={{ position: 'relative', zIndex: 1 }}>
          <Box sx={{ display: 'flex', flexDirection: { xs: 'column', md: 'row' }, gap: 4, alignItems: 'stretch' }}>
            {/* Left Column - First 3 Cards */}
            <Box sx={{ flex: 1, display: 'flex', flexDirection: 'column', gap: 4 }}>
              <Stack spacing={4}>
                {features.slice(0, 3).map((feature, index) => (
                  <Paper
                    key={index}
                    elevation={0}
                    sx={{
                      p: 3,
                      bgcolor: 'rgba(255, 255, 255, 0.8)',
                      backdropFilter: 'blur(10px)',
                      borderRadius: 3,
                      transition: 'all 0.3s ease-in-out',
                      border: '1px solid rgba(11,96,177,0.1)',
                      position: 'relative',
                      overflow: 'hidden',
                      '&::before': {
                        content: '""',
                        position: 'absolute',
                        top: 0,
                        left: 0,
                        width: '4px',
                        height: '100%',
                        background: 'linear-gradient(to bottom, #0B60B1, #2196F3)',
                        opacity: 0,
                        transition: 'opacity 0.3s ease-in-out'
                      },
                      '&:hover': {
                        transform: 'translateX(8px)',
                        boxShadow: '0 8px 32px rgba(11,96,177,0.1)',
                        borderColor: 'rgba(11,96,177,0.2)',
                        '&::before': {
                          opacity: 1
                        }
                      }
                    }}
                  >
                    <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                      <Box sx={{ 
                        p: 1.5, 
                        borderRadius: 2, 
                        background: 'linear-gradient(45deg, #0B60B1 30%, #2196F3 90%)',
                        color: 'white',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        boxShadow: '0 4px 12px rgba(11,96,177,0.2)'
                      }}>
                        {feature.icon}
                      </Box>
                      <Typography variant="h6" sx={{ ml: 2, fontWeight: 600 }}>
                        {feature.title}
                      </Typography>
                    </Box>
                    <Typography variant="body1" color="text.secondary">
                      {feature.description}
                    </Typography>
                  </Paper>
                ))}
              </Stack>
            </Box>

            {/* Middle Column - Image */}
            <Box sx={{ 
              flex: 1.5,
              display: 'flex', 
              alignItems: 'center',
              position: 'relative',
              '&::before': {
                content: '""',
                position: 'absolute',
                top: '-20px',
                right: '-20px',
                width: '200px',
                height: '200px',
                background: 'linear-gradient(45deg, #0B60B1 0%, #2196F3 100%)',
                borderRadius: '50%',
                opacity: 0.1,
                zIndex: 0
              }
            }}>
              <Paper
                elevation={0}
                sx={{
                  width: '100%',
                  height: '600px',
                  borderRadius: 4,
                  overflow: 'hidden',
                  position: 'relative',
                  bgcolor: '#f7fafd',
                  boxShadow: '0 20px 40px rgba(11,96,177,0.1)',
                  transform: 'perspective(1000px) rotateY(-15deg) rotateX(5deg)',
                  transition: 'all 0.5s ease-in-out',
                  '&:hover': {
                    transform: 'perspective(1000px) rotateY(-5deg) rotateX(2deg)',
                    boxShadow: '0 30px 60px rgba(11,96,177,0.2)',
                  }
                }}
              >
                <Box
                  component="img"
                  src="/images/phplist.png"
                  alt="PhpList Platform"
                  sx={{
                    width: '100%',
                    height: '100%',
                    objectFit: 'cover',
                    objectPosition: 'center top',
                    position: 'absolute',
                    top: 0,
                    left: 0,
                    transition: 'all 0.5s ease-in-out',
                    filter: 'brightness(0.95) contrast(1.1)',
                    '&:hover': {
                      filter: 'brightness(1) contrast(1.2)',
                    }
                  }}
                />
                <Box
                  sx={{
                    position: 'absolute',
                    top: 0,
                    left: 0,
                    right: 0,
                    bottom: 0,
                    background: 'linear-gradient(180deg, rgba(11,96,177,0.1) 0%, rgba(11,96,177,0) 50%, rgba(0,0,0,0.7) 100%)',
                    zIndex: 1
                  }}
                />
                <Box
                  sx={{
                    position: 'absolute',
                    bottom: 0,
                    left: 0,
                    right: 0,
                    p: 6,
                    zIndex: 2
                  }}
                >
                  <Box sx={{ 
                    display: 'flex', 
                    alignItems: 'center', 
                    mb: 3,
                    '&::before': {
                      content: '""',
                      width: '40px',
                      height: '4px',
                      background: 'linear-gradient(90deg, #0B60B1, #2196F3)',
                      borderRadius: '2px',
                      mr: 2
                    }
                  }}>
                    <Typography variant="h5" sx={{ 
                      fontWeight: 700, 
                      color: 'white',
                      textShadow: '0 2px 4px rgba(0,0,0,0.2)'
                    }}>
                      PhpList Platform
                    </Typography>
                  </Box>
                  <Typography variant="subtitle1" sx={{ 
                    color: 'white', 
                    opacity: 0.9, 
                    mb: 4,
                    textShadow: '0 1px 2px rgba(0,0,0,0.2)'
                  }}>
                    High-performance email marketing and list management
                  </Typography>
                </Box>
              </Paper>
            </Box>

            {/* Right Column - Last 3 Cards */}
            <Box sx={{ flex: 1, display: 'flex', flexDirection: 'column', gap: 4 }}>
              <Stack spacing={4}>
                {features.slice(3, 6).map((feature, index) => (
                  <Paper
                    key={index}
                    elevation={0}
                    sx={{
                      p: 3,
                      bgcolor: 'rgba(255, 255, 255, 0.8)',
                      backdropFilter: 'blur(10px)',
                      borderRadius: 3,
                      transition: 'all 0.3s ease-in-out',
                      border: '1px solid rgba(11,96,177,0.1)',
                      position: 'relative',
                      overflow: 'hidden',
                      '&::before': {
                        content: '""',
                        position: 'absolute',
                        top: 0,
                        left: 0,
                        width: '4px',
                        height: '100%',
                        background: 'linear-gradient(to bottom, #0B60B1, #2196F3)',
                        opacity: 0,
                        transition: 'opacity 0.3s ease-in-out'
                      },
                      '&:hover': {
                        transform: 'translateX(8px)',
                        boxShadow: '0 8px 32px rgba(11,96,177,0.1)',
                        borderColor: 'rgba(11,96,177,0.2)',
                        '&::before': {
                          opacity: 1
                        }
                      }
                    }}
                  >
                    <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                      <Box sx={{ 
                        p: 1.5, 
                        borderRadius: 2, 
                        background: 'linear-gradient(45deg, #0B60B1 30%, #2196F3 90%)',
                        color: 'white',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        boxShadow: '0 4px 12px rgba(11,96,177,0.2)'
                      }}>
                        {feature.icon}
                      </Box>
                      <Typography variant="h6" sx={{ ml: 2, fontWeight: 600 }}>
                        {feature.title}
                      </Typography>
                    </Box>
                    <Typography variant="body1" color="text.secondary">
                      {feature.description}
                    </Typography>
                  </Paper>
                ))}
              </Stack>
            </Box>
          </Box>
        </Container>
      </Box>

      {/* Service Categories */}
      <Box sx={{ bgcolor: '#f7fafd', py: 12, position: 'relative', overflow: 'hidden' }}>
        {/* Background decorative elements */}
        <Box sx={{
          position: 'absolute',
          top: 0,
          left: 0,
          width: '30%',
          height: '100%',
          background: 'linear-gradient(135deg, rgba(11,96,177,0.05) 0%, rgba(11,96,177,0) 100%)',
          clipPath: 'polygon(0 0, 100% 0, 0 100%, 0 0)',
          zIndex: 0
        }} />
        <Box sx={{
          position: 'absolute',
          bottom: 0,
          right: 0,
          width: '40%',
          height: '60%',
          background: 'linear-gradient(45deg, rgba(11,96,177,0.03) 0%, rgba(11,96,177,0) 100%)',
          clipPath: 'polygon(100% 0, 100% 100%, 0 100%, 100% 0)',
          zIndex: 0
        }} />

        <Container maxWidth="lg" sx={{ position: 'relative', zIndex: 1 }}>
          <Box sx={{ textAlign: 'center', mb: 8 }}>
            <Typography 
              variant="h4" 
              component="h2" 
              sx={{ 
                fontWeight: 700,
                background: 'linear-gradient(45deg, #0B60B1 30%, #2196F3 90%)',
                backgroundClip: 'text',
                textFillColor: 'transparent',
                WebkitBackgroundClip: 'text',
                WebkitTextFillColor: 'transparent',
                mb: 2
              }}
            >
              Our Services
            </Typography>
            <Typography variant="subtitle1" color="text.secondary" sx={{ maxWidth: '600px', mx: 'auto' }}>
              Comprehensive PhpList solutions tailored to your email marketing needs
            </Typography>
          </Box>

          <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: 'repeat(2, 1fr)', lg: 'repeat(3, 1fr)' }, gap: 3 }}>
            {serviceCategories.map((category, index) => (
              <Paper
                key={index}
                elevation={0}
                sx={{
                  p: 4,
                  height: '100%',
                  bgcolor: 'rgba(255, 255, 255, 0.8)',
                  backdropFilter: 'blur(10px)',
                  borderRadius: 3,
                  transition: 'all 0.3s ease-in-out',
                  border: '1px solid rgba(11,96,177,0.1)',
                  position: 'relative',
                  overflow: 'hidden',
                  '&::before': {
                    content: '""',
                    position: 'absolute',
                    top: 0,
                    left: 0,
                    width: '4px',
                    height: '100%',
                    background: 'linear-gradient(to bottom, #0B60B1, #2196F3)',
                    opacity: 0,
                    transition: 'opacity 0.3s ease-in-out'
                  },
                  '&:hover': {
                    transform: 'translateY(-8px)',
                    boxShadow: '0 12px 32px rgba(11,96,177,0.1)',
                    borderColor: 'rgba(11,96,177,0.2)',
                    '&::before': {
                      opacity: 1
                    }
                  }
                }}
              >
                <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
                  <Box sx={{ 
                    p: 1.5, 
                    borderRadius: 2, 
                    background: 'linear-gradient(45deg, #0B60B1 30%, #2196F3 90%)',
                    color: 'white',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    boxShadow: '0 4px 12px rgba(11,96,177,0.2)'
                  }}>
                    {category.icon}
                  </Box>
                  <Typography variant="h6" sx={{ ml: 2, fontWeight: 600 }}>
                    {category.title}
                  </Typography>
                </Box>
                <Stack spacing={2}>
                  {category.services.map((service, serviceIndex) => (
                    <Box 
                      key={serviceIndex} 
                      sx={{ 
                        display: 'flex', 
                        alignItems: 'center',
                        transition: 'all 0.3s ease-in-out',
                        '&:hover': {
                          transform: 'translateX(8px)'
                        }
                      }}
                    >
                      <CheckCircleIcon sx={{ 
                        color: 'primary.main', 
                        mr: 1.5, 
                        fontSize: 20,
                        transition: 'all 0.3s ease-in-out',
                        '&:hover': {
                          transform: 'scale(1.2)'
                        }
                      }} />
                      <Typography variant="body1" sx={{ color: 'text.secondary' }}>
                        {service}
                      </Typography>
                    </Box>
                  ))}
                </Stack>
              </Paper>
            ))}
          </Box>
        </Container>
      </Box>

      {/* FAQ Section */}
      <Box sx={{ py: 12, bgcolor: '#fff', position: 'relative' }}>
        <Container maxWidth="lg">
          <Box sx={{ textAlign: 'center', mb: 8 }}>
            <Typography 
              variant="h4" 
              component="h2" 
              sx={{ 
                fontWeight: 700,
                background: 'linear-gradient(45deg, #0B60B1 30%, #2196F3 90%)',
                backgroundClip: 'text',
                textFillColor: 'transparent',
                WebkitBackgroundClip: 'text',
                WebkitTextFillColor: 'transparent',
                mb: 2
              }}
            >
              Frequently Asked Questions
            </Typography>
            <Typography variant="subtitle1" color="text.secondary" sx={{ maxWidth: '600px', mx: 'auto' }}>
              Get answers to common questions about our PhpList consulting services
            </Typography>
          </Box>

          <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: 'repeat(2, 1fr)' }, gap: 4 }}>
            <Paper
              elevation={0}
              sx={{
                p: 4,
                bgcolor: 'rgba(255, 255, 255, 0.8)',
                backdropFilter: 'blur(10px)',
                borderRadius: 3,
                border: '1px solid rgba(11,96,177,0.1)',
                transition: 'all 0.3s ease-in-out',
                '&:hover': {
                  transform: 'translateY(-4px)',
                  boxShadow: '0 12px 32px rgba(11,96,177,0.1)',
                  borderColor: 'rgba(11,96,177,0.2)',
                }
              }}
            >
              <Typography variant="h6" sx={{ mb: 2, fontWeight: 600, color: 'primary.main' }}>
                What is PhpList?
              </Typography>
              <Typography variant="body1" color="text.secondary">
                PhpList is a powerful open-source email marketing and newsletter management system. It allows you to manage mailing lists, create and send newsletters, and track campaign performance.
              </Typography>
            </Paper>

            <Paper
              elevation={0}
              sx={{
                p: 4,
                bgcolor: 'rgba(255, 255, 255, 0.8)',
                backdropFilter: 'blur(10px)',
                borderRadius: 3,
                border: '1px solid rgba(11,96,177,0.1)',
                transition: 'all 0.3s ease-in-out',
                '&:hover': {
                  transform: 'translateY(-4px)',
                  boxShadow: '0 12px 32px rgba(11,96,177,0.1)',
                  borderColor: 'rgba(11,96,177,0.2)',
                }
              }}
            >
              <Typography variant="h6" sx={{ mb: 2, fontWeight: 600, color: 'primary.main' }}>
                How long does implementation take?
              </Typography>
              <Typography variant="body1" color="text.secondary">
                Implementation time varies based on your specific needs. A basic setup typically takes 1-2 weeks, while a full custom implementation with advanced features may take 4-6 weeks.
              </Typography>
            </Paper>

            <Paper
              elevation={0}
              sx={{
                p: 4,
                bgcolor: 'rgba(255, 255, 255, 0.8)',
                backdropFilter: 'blur(10px)',
                borderRadius: 3,
                border: '1px solid rgba(11,96,177,0.1)',
                transition: 'all 0.3s ease-in-out',
                '&:hover': {
                  transform: 'translateY(-4px)',
                  boxShadow: '0 12px 32px rgba(11,96,177,0.1)',
                  borderColor: 'rgba(11,96,177,0.2)',
                }
              }}
            >
              <Typography variant="h6" sx={{ mb: 2, fontWeight: 600, color: 'primary.main' }}>
                Do you provide ongoing support?
              </Typography>
              <Typography variant="body1" color="text.secondary">
                Yes, we offer comprehensive support packages including regular maintenance, updates, and 24/7 technical assistance to ensure your PhpList installation runs smoothly.
              </Typography>
            </Paper>

            <Paper
              elevation={0}
              sx={{
                p: 4,
                bgcolor: 'rgba(255, 255, 255, 0.8)',
                backdropFilter: 'blur(10px)',
                borderRadius: 3,
                border: '1px solid rgba(11,96,177,0.1)',
                transition: 'all 0.3s ease-in-out',
                '&:hover': {
                  transform: 'translateY(-4px)',
                  boxShadow: '0 12px 32px rgba(11,96,177,0.1)',
                  borderColor: 'rgba(11,96,177,0.2)',
                }
              }}
            >
              <Typography variant="h6" sx={{ mb: 2, fontWeight: 600, color: 'primary.main' }}>
                Can you integrate with other systems?
              </Typography>
              <Typography variant="body1" color="text.secondary">
                Absolutely! We can integrate PhpList with various CRM systems, e-commerce platforms, and other marketing tools to create a seamless workflow for your business.
              </Typography>
            </Paper>
          </Box>
        </Container>
      </Box>

      {/* Consultation Form Section */}
      <Container maxWidth="lg">
        <ConsultationForm
          title="Ready to Transform Your Email Marketing?"
          formName="phplist_consultation"
          formSource="phplist_page"
          bulletPoints={[
            'Set up and optimize your PhpList instance',
            'Configure email campaign management',
            'Implement subscriber segmentation',
            'Set up campaign tracking and analytics',
            'Integrate with your existing tools',
            'Train your team on best practices'
          ]}
          additionalFields={[
            {
              name: 'subscriber_count',
              label: 'Current Subscriber Count',
              type: 'select',
              required: true,
              options: [
                { value: '0-1000', label: '0 - 1,000 subscribers' },
                { value: '1000-5000', label: '1,000 - 5,000 subscribers' },
                { value: '5000-10000', label: '5,000 - 10,000 subscribers' },
                { value: '10000-50000', label: '10,000 - 50,000 subscribers' },
                { value: '50000+', label: '50,000+ subscribers' }
              ]
            },
            {
              name: 'current_platform',
              label: 'Current Email Platform',
              type: 'select',
              required: true,
              options: [
                { value: 'none', label: 'No current platform' },
                { value: 'mailchimp', label: 'Mailchimp' },
                { value: 'sendinblue', label: 'Sendinblue' },
                { value: 'mailerlite', label: 'MailerLite' },
                { value: 'convertkit', label: 'ConvertKit' },
                { value: 'other', label: 'Other' }
              ]
            },
            {
              name: 'campaign_type',
              label: 'Primary Campaign Type',
              type: 'select',
              required: true,
              options: [
                { value: 'newsletter', label: 'Newsletter' },
                { value: 'promotional', label: 'Promotional Emails' },
                { value: 'transactional', label: 'Transactional Emails' },
                { value: 'drip', label: 'Drip Campaigns' },
                { value: 'automated', label: 'Automated Sequences' }
              ]
            }
          ]}
        />
      </Container>
    </Box>
  );
} 